import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  paymentMethods,
  type BillingAccountSummaryView,
  type LedgerReportCurrencyView,
  type LedgerReportFiltersView,
  type LedgerReportView,
  type ReportChargeTotalsView,
  type ReportPaymentTotalsView,
  type SupportedLocale,
} from '@stm/contracts';
import { translate } from '@stm/i18n';
import { apiRequest } from './api';
import { formatMoney, paymentMethodKeys } from './billing';
import {
  emptyReportFilters,
  filtersWithMethod,
  filtersWithSeason,
  filtersWithTeam,
  ledgerReportCsv,
  reconciliationCheckKeys,
  reportQueryString,
  type ReportFilterChoices,
} from './billingReport';
import { errorKeyOf, type ErrorKey } from './BillingForms';

export interface BillingReportsPanelProps {
  locale: SupportedLocale;
  organizationId: string;
  /** The accounts loaded so far, offered as the account filter. */
  accounts: readonly BillingAccountSummaryView[];
}

function ChargeRow({
  label,
  totals,
  currency,
  locale,
  strong = false,
}: {
  label: string;
  totals: ReportChargeTotalsView;
  currency: string;
  locale: SupportedLocale;
  strong?: boolean;
}): React.JSX.Element {
  const money = (minor: string): string => formatMoney(minor, currency, locale);
  const sx = strong ? { fontWeight: 600 } : undefined;
  return (
    <TableRow>
      <TableCell sx={sx}>{label}</TableCell>
      <TableCell align="right" sx={sx}>
        {totals.count}
      </TableCell>
      <TableCell align="right" sx={sx}>
        {money(totals.chargedMinor)}
      </TableCell>
      <TableCell align="right" sx={sx}>
        {money(totals.creditedMinor)}
      </TableCell>
      <TableCell align="right" sx={sx}>
        {money(totals.allocatedMinor)}
      </TableCell>
      <TableCell align="right" sx={sx}>
        {money(totals.outstandingMinor)}
      </TableCell>
      <TableCell align="right" sx={sx}>
        {money(totals.overdueMinor)}
      </TableCell>
      <TableCell align="right" sx={sx}>
        {money(totals.upcomingMinor)}
      </TableCell>
    </TableRow>
  );
}

function PaymentRow({
  label,
  totals,
  currency,
  locale,
  strong = false,
}: {
  label: string;
  totals: ReportPaymentTotalsView;
  currency: string;
  locale: SupportedLocale;
  strong?: boolean;
}): React.JSX.Element {
  const money = (minor: string): string => formatMoney(minor, currency, locale);
  const sx = strong ? { fontWeight: 600 } : undefined;
  return (
    <TableRow>
      <TableCell sx={sx}>{label}</TableCell>
      <TableCell align="right" sx={sx}>
        {totals.count}
      </TableCell>
      <TableCell align="right" sx={sx}>
        {money(totals.receivedMinor)}
      </TableCell>
      <TableCell align="right" sx={sx}>
        {money(totals.reversedMinor)}
      </TableCell>
      <TableCell align="right" sx={sx}>
        {money(totals.allocatedMinor)}
      </TableCell>
      <TableCell align="right" sx={sx}>
        {money(totals.refundedMinor)}
      </TableCell>
      <TableCell align="right" sx={sx}>
        {money(totals.unallocatedCreditMinor)}
      </TableCell>
    </TableRow>
  );
}

function CurrencyBlock({
  block,
  locale,
}: {
  block: LedgerReportCurrencyView;
  locale: SupportedLocale;
}): React.JSX.Element {
  const label = (key: Parameters<typeof translate>[1]): string => translate(locale, key);
  return (
    <Box component="section" aria-label={block.currency} sx={{ mb: 3 }}>
      <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600 }} gutterBottom>
        {label('billing.reports.currency')} {block.currency} · {label('billing.reports.accounts')}{' '}
        {block.accountCount}
      </Typography>
      {block.charges === null ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {label('billing.reports.chargesOmitted')}
        </Alert>
      ) : (
        <TableContainer sx={{ mb: 2 }}>
          <Table size="small" aria-label={label('billing.reports.byTeam')}>
            <caption>{label('billing.reports.byTeam')}</caption>
            <TableHead>
              <TableRow>
                <TableCell>{label('billing.reports.col.team')}</TableCell>
                <TableCell align="right">{label('billing.reports.col.count')}</TableCell>
                <TableCell align="right">{label('billing.reports.col.charged')}</TableCell>
                <TableCell align="right">{label('billing.reports.col.credited')}</TableCell>
                <TableCell align="right">{label('billing.reports.col.allocated')}</TableCell>
                <TableCell align="right">{label('billing.col.outstanding')}</TableCell>
                <TableCell align="right">{label('billing.position.overdue')}</TableCell>
                <TableCell align="right">{label('billing.position.upcoming')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {block.charges.byTeam.map((team) => (
                <ChargeRow
                  key={team.teamSeasonId ?? 'none'}
                  label={team.teamName ?? label('billing.reports.noTeam')}
                  totals={team.totals}
                  currency={block.currency}
                  locale={locale}
                />
              ))}
              <ChargeRow
                label={label('billing.reports.total')}
                totals={block.charges.totals}
                currency={block.currency}
                locale={locale}
                strong
              />
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {block.payments === null ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {label('billing.reports.paymentsOmitted')}
        </Alert>
      ) : (
        <TableContainer sx={{ mb: 2 }}>
          <Table size="small" aria-label={label('billing.reports.byMethod')}>
            <caption>{label('billing.reports.byMethod')}</caption>
            <TableHead>
              <TableRow>
                <TableCell>{label('billing.reports.col.method')}</TableCell>
                <TableCell align="right">{label('billing.reports.col.count')}</TableCell>
                <TableCell align="right">{label('billing.reports.col.received')}</TableCell>
                <TableCell align="right">{label('billing.reports.col.reversed')}</TableCell>
                <TableCell align="right">{label('billing.reports.col.allocated')}</TableCell>
                <TableCell align="right">{label('billing.reports.col.refunded')}</TableCell>
                <TableCell align="right">{label('billing.position.credit')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {block.payments.byMethod.map((method) => (
                <PaymentRow
                  key={method.method}
                  label={label(paymentMethodKeys[method.method])}
                  totals={method.totals}
                  currency={block.currency}
                  locale={locale}
                />
              ))}
              <PaymentRow
                label={label('billing.reports.total')}
                totals={block.payments.totals}
                currency={block.currency}
                locale={locale}
                strong
              />
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Typography variant="subtitle2" component="h4" gutterBottom>
        {label('billing.reports.reconciliation.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {label('billing.reports.reconciliation.note')}
      </Typography>
      <TableContainer>
        <Table size="small" aria-label={label('billing.reports.reconciliation.title')}>
          <TableHead>
            <TableRow>
              <TableCell>{label('billing.reports.reconciliation.check')}</TableCell>
              <TableCell align="right">
                {label('billing.reports.reconciliation.expected')}
              </TableCell>
              <TableCell align="right">{label('billing.reports.reconciliation.actual')}</TableCell>
              <TableCell>{label('billing.reports.reconciliation.result')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {block.reconciliation.map((check) => (
              <TableRow key={check.code}>
                <TableCell>{label(reconciliationCheckKeys[check.code])}</TableCell>
                <TableCell align="right">
                  {formatMoney(check.expectedMinor, block.currency, locale)}
                </TableCell>
                <TableCell align="right">
                  {formatMoney(check.actualMinor, block.currency, locale)}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={check.ok ? 'success' : 'error'}
                    label={label(
                      check.ok
                        ? 'billing.reports.reconciliation.ok'
                        : 'billing.reports.reconciliation.mismatch',
                    )}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

/**
 * Finance reports: the whole club's charges and payments as the ledger stood at the end of a chosen
 * day, filtered by season, team at the due date, payment method and account, with a reconciliation
 * of the ledger's views and an audited CSV download. Currencies are never summed.
 */
export function BillingReportsPanel({
  locale,
  organizationId,
  accounts,
}: BillingReportsPanelProps): React.JSX.Element {
  const base = `/api/v1/organizations/${organizationId}/billing/reports`;
  const [filters, setFilters] = useState<ReportFilterChoices>(emptyReportFilters);
  const [options, setOptions] = useState<LedgerReportFiltersView>({ seasons: [], teams: [] });
  const [report, setReport] = useState<LedgerReportView | null>(null);
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);

  useEffect(() => {
    let current = true;
    apiRequest<LedgerReportFiltersView>(`${base}/filters`)
      .then((loaded) => {
        if (current) setOptions(loaded);
      })
      .catch((error: unknown) => {
        if (current) setErrorKey(errorKeyOf(error));
      });
    return () => {
      current = false;
    };
  }, [base]);

  useEffect(() => {
    let current = true;
    setReport(null);
    setErrorKey(null);
    apiRequest<LedgerReportView>(`${base}/ledger${reportQueryString(filters)}`)
      .then((loaded) => {
        if (current) setReport(loaded);
      })
      .catch((error: unknown) => {
        if (current) setErrorKey(errorKeyOf(error));
      });
    return () => {
      current = false;
    };
  }, [base, filters]);

  const teamsOfSeason = options.teams.filter(
    (team) => filters.seasonId === '' || team.seasonId === filters.seasonId,
  );

  async function download(): Promise<void> {
    setErrorKey(null);
    try {
      const exported = await apiRequest<LedgerReportView>(
        `${base}/ledger/export${reportQueryString(filters)}`,
      );
      const csv = ledgerReportCsv(exported, locale, {
        season: options.seasons.find((season) => season.id === filters.seasonId)?.label ?? null,
        team: options.teams.find((team) => team.id === filters.teamSeasonId)?.name ?? null,
        account: accounts.find((account) => account.id === filters.accountId)?.label ?? null,
      });
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `finance-report-${exported.asOf}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }

  return (
    <Box component="section" aria-labelledby="billing-reports-title" sx={{ mt: 4, mb: 3 }}>
      <Typography variant="h6" component="h2" id="billing-reports-title" gutterBottom>
        {translate(locale, 'billing.reports.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {translate(locale, 'billing.reports.note')}
      </Typography>
      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <TextField
          size="small"
          type="date"
          label={translate(locale, 'billing.reports.asOf')}
          helperText={translate(locale, 'billing.reports.asOfHelp')}
          value={filters.asOf}
          onChange={(event) => {
            setFilters({ ...filters, asOf: event.target.value });
          }}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          select
          size="small"
          label={translate(locale, 'billing.reports.season')}
          value={filters.seasonId}
          onChange={(event) => {
            setFilters(filtersWithSeason(filters, event.target.value, options.teams));
          }}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">{translate(locale, 'billing.reports.any')}</MenuItem>
          {options.seasons.map((season) => (
            <MenuItem key={season.id} value={season.id}>
              {season.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label={translate(locale, 'billing.reports.team')}
          value={filters.teamSeasonId}
          onChange={(event) => {
            setFilters(filtersWithTeam(filters, event.target.value));
          }}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">{translate(locale, 'billing.reports.any')}</MenuItem>
          {teamsOfSeason.map((team) => (
            <MenuItem key={team.id} value={team.id}>
              {team.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label={translate(locale, 'billing.reports.method')}
          value={filters.method}
          onChange={(event) => {
            setFilters(
              filtersWithMethod(
                filters,
                paymentMethods.find((method) => method === event.target.value) ?? '',
              ),
            );
          }}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">{translate(locale, 'billing.reports.any')}</MenuItem>
          {paymentMethods.map((method) => (
            <MenuItem key={method} value={method}>
              {translate(locale, paymentMethodKeys[method])}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label={translate(locale, 'billing.reports.account')}
          value={filters.accountId}
          onChange={(event) => {
            setFilters({ ...filters, accountId: event.target.value });
          }}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">{translate(locale, 'billing.reports.any')}</MenuItem>
          {accounts.map((account) => (
            <MenuItem key={account.id} value={account.id}>
              {account.label}
            </MenuItem>
          ))}
        </TextField>
        <Button
          variant="outlined"
          disabled={report === null}
          onClick={() => {
            void download();
          }}
        >
          {translate(locale, 'billing.reports.export')}
        </Button>
      </Stack>
      {errorKey !== null && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {translate(locale, errorKey)}
        </Alert>
      )}
      {errorKey === null && report === null && (
        <Typography>{translate(locale, 'billing.reports.loading')}</Typography>
      )}
      {report?.currencies.length === 0 && (
        <Typography>{translate(locale, 'billing.reports.empty')}</Typography>
      )}
      {report?.currencies.map((block) => (
        <CurrencyBlock key={block.currency} block={block} locale={locale} />
      ))}
    </Box>
  );
}
